import {
  AUTH_ERROR_PARAM,
  authErrorCodeSchema,
  currentUserSchema,
  logoutResponseSchema,
  type AuthErrorCode,
  type CurrentUser,
  type LogoutResponse,
} from '@gamble-chess/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, requestJson } from './client.ts';
import { queryKeys } from './queryKeys.ts';

/** A full-page navigation, not a fetch: the server redirects the browser to the provider. */
export const SIGN_IN_URL = '/api/auth/login';

/** Resolves to `null` for anonymous visitors, which is an expected state rather than an error. */
export async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser | null> {
  const response = await fetch('/api/me', signal === undefined ? {} : { signal });

  if (response.status === 401) {
    return null;
  }
  if (!response.ok) {
    throw new ApiError(
      `Loading the current user failed with ${String(response.status)}`,
      response.status,
    );
  }

  return currentUserSchema.parse(await response.json());
}

export function useCurrentUser(): UseQueryResult<CurrentUser | null> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
  });
}

/** The zone rule guards are read in; changing it changes what the rules say (ADR-0019). */
export function useSetTimeZone(): UseMutationResult<CurrentUser, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (timeZone) =>
      requestJson('/api/me', currentUserSchema, 'Saving your time zone failed. Please try again.', {
        method: 'PATCH',
        body: { timeZone },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export async function postLogout(): Promise<LogoutResponse> {
  const response = await fetch('/api/auth/logout', { method: 'POST' });

  if (!response.ok) {
    throw new ApiError(`Sign out failed with ${String(response.status)}`, response.status);
  }

  return logoutResponseSchema.parse(await response.json());
}

/** Ends the app session, then hands the browser to the provider to end its session too. */
export function useSignOut(): UseMutationResult<LogoutResponse, Error, void> {
  return useMutation({
    mutationFn: postLogout,
    onSuccess: ({ logoutUrl }) => {
      window.location.assign(logoutUrl);
    },
  });
}

export function readAuthError(search: string): AuthErrorCode | undefined {
  const parsed = authErrorCodeSchema.safeParse(new URLSearchParams(search).get(AUTH_ERROR_PARAM));
  return parsed.success ? parsed.data : undefined;
}
